import type { DashboardItem, DashboardItemDataType, OldDashboardItem } from 'types';
import type { AgendaViewItem, KpiItem } from 'types/dashboard';
import type { Module } from 'types/modules';
import { getUUID } from './common';

export const formatDashboardData = <T>(data: T | null | undefined): DashboardItem[] => {
  if (!data) return [];

  let rawItems: DashboardItem[];

  if (Array.isArray(data)) {
    const isOldFormat = data.some((item) => ['info', 'activities', 'modules'].includes(item?.type));

    rawItems = isOldFormat ? transformOldDashboard(data as unknown as OldDashboardItem[]) : (data as unknown as DashboardItem[]);
  } else {
    rawItems = Object.entries(data as Record<string, unknown>).map(
      ([key, value]) =>
        ({
          ...(value as Record<string, unknown>),
          type: key,
        }) as DashboardItem
    );
  }

  let currentMaxRow = rawItems.reduce((max, item) => {
    return item?.row != null ? Math.max(max, item.row) : max;
  }, -1);

  return rawItems
    .map((item) => {
      let row = item?.row;

      if (row == null) {
        currentMaxRow += 1;
        row = currentMaxRow;
      }

      return {
        ...item,
        row,
        items: (item?.items ?? []).map((x) => ({
          ...x,
          id: x.id ?? getUUID(),
        })),
      } as DashboardItem;
    })
    .sort((a, b) => (a.row as number) - (b.row as number));
};

export const transformOldDashboard = (oldDashboard: OldDashboardItem[]): DashboardItem[] => {
  const mapToAgendaItems = (data: DashboardItemDataType[]) => {
    return data.map((item, index) => ({
      order: index + 1,
      borderColor: '',
      type: 'AgendaView',
      kpi: null,
      agendaView: {
        moduleId: item.moduleId,
        agendaId: item.agendaId,
        viewId: item.viewId,
      },
    }));
  };

  const mapToKpiItems = (data: DashboardItemDataType[]) => {
    return data.map((item, index) => ({
      order: index + 1,
      borderColor: '',
      type: 'KPI',
      agendaView: null,
      kpi: {
        link: null,
        view: {
          moduleId: item.moduleId,
          agendaId: item.agendaId,
          viewId: item.viewId,
        },
      },
    }));
  };

  return oldDashboard.map((item, index) => {
    const row = index + 1;

    if (item.type === 'activities') {
      return {
        type: 'agendaViewItems',
        row,
        items: mapToAgendaItems(item.data ?? []),
      } as unknown as DashboardItem;
    }

    if (item.type === 'info') {
      return {
        type: 'kpiItems',
        row,
        items: mapToKpiItems(item.data ?? []),
      } as unknown as DashboardItem;
    }

    if (item.type === 'modules') {
      return {
        type: 'modules',
        row,
        viewMode: 'viewMode' in item ? item.viewMode : 'module',
      } as DashboardItem;
    }

    return item as unknown as DashboardItem;
  });
};

const collectAccessibleAgendaIds = (modules: Module[], ids: Set<string>) => {
  modules.forEach((module) => {
    (module.agendas ?? []).forEach((agenda) => ids.add(agenda.agendaId.toLowerCase()));
    collectAccessibleAgendaIds(module.childs ?? [], ids);
  });
};

export const extractAccessibleAgendaIds = (modules: Module[] = []): Set<string> => {
  const accessibleAgendaIds = new Set<string>();

  collectAccessibleAgendaIds(modules, accessibleAgendaIds);

  return accessibleAgendaIds;
};

const collectAccessibleModuleIds = (modules: Module[], ids: Set<string>) => {
  modules.forEach((module) => {
    ids.add(module.moduleId);
    collectAccessibleModuleIds(module.childs ?? [], ids);
  });
};

export const extractAccessibleModuleIds = (modules: Module[] = []): Set<string> => {
  const accessibleModuleIds = new Set<string>();

  collectAccessibleModuleIds(modules, accessibleModuleIds);

  return accessibleModuleIds;
};

const getKpiAgendaId = (item: KpiItem): string | undefined => item.kpi?.view?.agendaId ?? item.kpi?.heliosSqlScript?.agendaId;

const NON_AGENDA_WIDGET_TYPES: AgendaViewItem['type'][] = ['Notification', 'NotificationWithAction'];

const isNonAgendaWidget = (item: AgendaViewItem) => NON_AGENDA_WIDGET_TYPES.includes(item.type);

export const filterDashboardByAccessibleAgendas = (items: DashboardItem[], accessibleAgendaIds: Set<string>): DashboardItem[] => {
  const hasAccess = (agendaId?: string | null) => agendaId == null || accessibleAgendaIds.has(agendaId.toLowerCase());

  return items.flatMap((item): DashboardItem[] => {
    if (item.type === 'agendaViewItems') {
      const visibleItems = item.items.filter((entry) => isNonAgendaWidget(entry) || hasAccess(entry.agendaView?.agendaId));
      return visibleItems.length > 0 ? [{ ...item, items: visibleItems }] : [];
    }

    if (item.type === 'kpiItems') {
      const visibleItems = item.items.filter((entry) => hasAccess(getKpiAgendaId(entry)));
      return visibleItems.length > 0 ? [{ ...item, items: visibleItems }] : [];
    }

    return [item];
  });
};
